import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Send, Phone, Calculator, X, Clock, Paperclip, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/contact";
import { scrollToId } from "./Nav";
import { BookCall } from "./BookCall";

const empty = { name: "", email: "", phone: "", city: "", postal_code: "", interest: "mobile", message: "", referred_by: "" };

export const LeadForm = () => {
  const { t, lang } = useLang();
  const f = t.form;
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sim, setSim] = useState("");
  const [respHours, setRespHours] = useState(null);
  const [bill, setBill] = useState(null);
  const [billUploading, setBillUploading] = useState(false);
  const [teaser, setTeaser] = useState(null);

  const onBill = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBillUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads/bill", fd);
      setBill(res.data);
      setTeaser("loading");
      api.post("/uploads/bill/analyze", { path: res.data.path, lang })
        .then((a) => setTeaser(a.data))
        .catch(() => setTeaser(null));
    } catch {
      toast.error(f.billError);
    } finally {
      setBillUploading(false);
    }
  };

  useEffect(() => {
    api.get("/stats/response-time").then((res) => setRespHours(res.data.avg_hours)).catch(() => {});
  }, []);

  useEffect(() => {
    setSim(sessionStorage.getItem("sta_sim") || "");
    const onSim = (e) => setSim(e.detail);
    window.addEventListener("sta-sim", onSim);
    return () => window.removeEventListener("sta-sim", onSim);
  }, []);

  const clearSim = () => {
    sessionStorage.removeItem("sta_sim");
    setSim("");
  };

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      setForm((prev) => ({ ...prev, referred_by: ref }));
      setTimeout(() => scrollToId("contact"), 800);
    }
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", { ...form, simulated_pack: sim, bill_path: bill?.path || "", lang });
      setSent(true);
      clearSim();
      setBill(null);
      setTeaser(null);
    } catch (err) {
      toast.error(f.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-[#0a0a0a] border border-white/15 focus:border-[#FF7900] outline-none px-5 py-4 text-white placeholder:text-white/30 transition-colors duration-300 rounded-none";

  return (
    <section id="contact" className="py-24 md:py-32 border-t border-white/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-[#FF7900] mb-6">{f.overline}</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight mb-8">{f.title}</h2>
          <p className="text-base md:text-lg text-white/70 leading-relaxed max-w-md">{f.sub}</p>
          <p className="mt-8 text-sm text-white/40">{f.phoneDirect}</p>
          <a
            href={PHONE_TEL}
            data-testid="contact-call-button"
            className="mt-4 inline-flex items-center gap-3 border border-[#FF7900]/50 hover:bg-[#FF7900] text-[#FF7900] hover:text-white font-semibold px-6 py-3 rounded-full transition-colors duration-300"
          >
            <Phone className="w-4 h-4" />
            {PHONE_DISPLAY}
          </a>
          <BookCall />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="bg-[#111111] border border-white/10 p-8 md:p-10"
        >
          {sent ? (
            <div className="flex flex-col items-start gap-6 py-10" data-testid="lead-success">
              <CheckCircle2 className="w-14 h-14 text-[#FF7900]" />
              <h3 className="font-display text-3xl font-medium">{f.successTitle}</h3>
              <p className="text-white/70 leading-relaxed">{f.successText}</p>
              <button
                onClick={() => {
                  setForm(empty);
                  setSent(false);
                }}
                data-testid="lead-again-button"
                className="mt-2 border border-white/25 hover:border-[#FF7900] hover:text-[#FF7900] px-6 py-3 rounded-full text-sm font-semibold transition-colors duration-300"
              >
                {f.again}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-5" data-testid="lead-form">
              <p className="flex items-center gap-2 text-xs font-semibold text-emerald-400" data-testid="response-time-badge">
                <Clock className="w-3.5 h-3.5" />
                {f.responseBadge.replace("{h}", String(respHours ? Math.max(1, Math.ceil(respHours)) : 24))}
              </p>
              {sim && (
                <div className="flex items-start justify-between gap-3 border border-[#FF7900]/40 bg-[#FF7900]/10 px-4 py-3" data-testid="lead-sim-chip">
                  <p className="flex items-start gap-2 text-sm text-[#FF7900]">
                    <Calculator className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      <span className="block text-[11px] uppercase tracking-widest opacity-70">{f.simLabel}</span>
                      {sim}
                    </span>
                  </p>
                  <button type="button" onClick={clearSim} data-testid="lead-sim-remove" aria-label="Remove" className="text-[#FF7900]/60 hover:text-[#FF7900] transition-colors duration-300">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <input required value={form.name} onChange={set("name")} placeholder={f.name} className={inputCls} data-testid="lead-name-input" />
              <div className="grid sm:grid-cols-2 gap-5">
                <input required type="email" value={form.email} onChange={set("email")} placeholder={f.email} className={inputCls} data-testid="lead-email-input" />
                <input required type="tel" value={form.phone} onChange={set("phone")} placeholder={f.phone} className={inputCls} data-testid="lead-phone-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <input value={form.city} onChange={set("city")} placeholder={f.city} className={inputCls} data-testid="lead-city-input" />
                <input
                  value={form.postal_code}
                  onChange={(ev) => setForm((s) => ({ ...s, postal_code: ev.target.value.replace(/\D/g, "").slice(0, 4) }))}
                  placeholder={f.postal}
                  inputMode="numeric"
                  maxLength={4}
                  className={inputCls}
                  data-testid="lead-postal-input"
                />
              </div>
              <input value={form.referred_by} onChange={set("referred_by")} placeholder={f.referredBy} className={inputCls} data-testid="lead-referred-by-input" />
              <select value={form.interest} onChange={set("interest")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="lead-interest-select">
                {Object.entries(f.options).map(([k, v]) => (
                  <option key={k} value={k} className="bg-[#111]">
                    {v}
                  </option>
                ))}
              </select>
              <textarea value={form.message} onChange={set("message")} placeholder={f.message} rows={4} className={`${inputCls} resize-none`} data-testid="lead-message-textarea" />
              {bill ? (
                <div className="flex items-center justify-between gap-3 border border-emerald-500/40 bg-emerald-500/10 px-4 py-3" data-testid="lead-bill-chip">
                  <p className="flex items-center gap-2 text-sm text-emerald-400 min-w-0">
                    <Paperclip className="w-4 h-4 shrink-0" />
                    <span className="truncate">{bill.filename}</span>
                  </p>
                  <button type="button" onClick={() => { setBill(null); setTeaser(null); }} data-testid="lead-bill-remove" aria-label="Remove" className="text-emerald-400/60 hover:text-emerald-400 transition-colors duration-300">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-3 border border-dashed border-white/20 hover:border-[#FF7900]/60 px-5 py-4 cursor-pointer text-sm text-white/50 hover:text-white/80 transition-colors duration-300" data-testid="lead-bill-label">
                  <Paperclip className="w-4 h-4 text-[#FF7900] shrink-0" />
                  {billUploading ? f.billUploading : f.billLabel}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={onBill} disabled={billUploading} className="hidden" data-testid="lead-bill-input" />
                </label>
              )}
              {bill && teaser === "loading" && (
                <p className="flex items-center gap-2 text-xs text-white/40 animate-pulse" data-testid="bill-teaser-loading">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                  {f.billAnalyzing}
                </p>
              )}
              {bill && teaser && teaser !== "loading" && teaser.monthly_savings > 0 && (
                <div className="border border-emerald-500/40 bg-emerald-500/10 px-4 py-3" data-testid="bill-savings-teaser">
                  <p className="flex items-start gap-2 text-sm text-emerald-400">
                    <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{f.billTeaser.replace("{x}", String(Math.round(teaser.monthly_savings)))}</span>
                  </p>
                </div>
              )}
              <button
                type="submit"
                disabled={sending}
                data-testid="lead-submit-button"
                className="group mt-2 inline-flex items-center justify-center gap-3 bg-[#FF7900] hover:bg-white hover:text-black disabled:opacity-50 text-white font-semibold px-8 py-4 rounded-full transition-colors duration-300"
              >
                {sending ? f.sending : f.submit}
                <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
